/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import Emoji from 'react-emoji-render';
import Marquee from 'react-fast-marquee';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { TRACKING_LABELS } from '@/utils';

import { ReactComponent as ArrowRight } from '../../images/icons/icon-arrow-right.svg';

import { LinkWithQuery } from './LinkWithQuery';

const Wrapper = styled(motion.div)<{ $justOne: boolean }>`
  display: flex;
  justify-content: center;
  height: 46px;
  width: 100%;
  background-color: ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.1)};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};

  ${({ $justOne }) =>
    $justOne &&
    css`
      height: auto;
      min-height: 61px;
      padding: 20px 0;
      font-size: ${({ theme }) => theme.fontSize.MEDIUM};

      .rfm-marquee-container {
        width: auto;
        justify-content: center;
        padding-left: 0;
        overflow-y: hidden !important;

        > :first-child {
          display: none;
        }

        .rfm-marquee {
          max-width: 100%;
        }
      }

      .text {
        margin-left: 10px;
      }
    `};
`;

const Item = styled(LinkWithQuery)`
  display: flex;
  align-items: center;
  line-height: 24px;
  border: none;
  margin: 0 20px;

  > span:first-child {
    display: flex;
    align-items: center;
    line-height: 19px;
    height: 18px;
    width: 18px;
    margin-right: 2px;
  }
`;

const Text = styled.div`
  margin-left: 7px;
  margin-right: 5px;
`;

const EmojiWrapper = styled.div`
  span {
    span {
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      vertical-align: 0 !important;
    }
  }
`;

const Title = styled.span`
  color: ${({ theme }) => theme.fontColor.LOUD};
`;

const Arrow = styled.div`
  display: flex;
  align-items: center;
  height: 18px;
  padding-top: 1px;

  svg path {
    fill: ${({ theme }) => theme.highlightColor};
  }
`;

interface TickerItem {
  emoji: string;
  link: string;
  secondaryText: string;
  title: string;
}

export const Ticker: React.ComponentType = React.memo(() => {
  const data = useStaticQuery(pageQuery);
  const allItems = [...data.allContentfulTicker.nodes];

  return (
    <Wrapper
      $justOne={allItems.length === 1}
      initial={{ opacity: 0 }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}>
      <Marquee
        gradient={false}
        pauseOnClick={true}
        pauseOnHover={true}
        play={allItems.length > 1}
        speed={40}>
        {allItems.map((item: TickerItem, index: number) => (
          <Item
            key={index}
            isExternal={item.link.includes('https')}
            to={item.link}
            trackingLabel={TRACKING_LABELS.TickerItem}>
            <EmojiWrapper>
              <Emoji text={item.emoji} />
            </EmojiWrapper>
            <Text>
              <Title>{item.title}</Title> {item.secondaryText}
            </Text>
            <Arrow>
              <ArrowRight />
            </Arrow>
          </Item>
        ))}
      </Marquee>
    </Wrapper>
  );
});

const pageQuery = graphql`
  query {
    allContentfulTicker {
      nodes {
        title
        secondaryText
        emoji
        link
      }
    }
  }
`;
